/**
 * Creates a module-level external store tracking a Set of keys,
 * compatible with `useSyncExternalStore`.
 *
 * Usage:
 * ```ts
 * const store = createExternalSetStore();
 *
 * // In invalidation function:
 * store.add(exchangeId);
 *
 * // In hook:
 * const isInvalidating = useSyncExternalStore(store.subscribe, () => store.has(exchangeId));
 *
 * // When fetch completes:
 * store.delete(exchangeId);
 * ```
 */
export function createExternalSetStore() {
  const set = new Set<string>();
  const listeners = new Set<() => void>();

  function notify() {
    listeners.forEach((l) => l());
  }

  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    has(key: string): boolean {
      return set.has(key);
    },
    add(key: string) {
      set.add(key);
      notify();
    },
    delete(key: string) {
      if (set.delete(key)) {
        notify();
      }
    },
  };
}
